import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  DOCUMENT_FORM_NAME_BALANCE_SHEET,
  DOCUMENT_FORM_NAME_BASE_ACRES,
  DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
  DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY,
  DOCUMENT_FORM_NAME_INCOME_STATEMENT,
  DOCUMENT_FORM_NAME_SCHEDULE_F,
} from "@datanac/datanac-api-toolkit";
import {
  Cancel,
  Close,
  DataObject,
  Description,
  Download,
  Edit,
  RadioButtonChecked,
  Save,
  Search,
  TableChart,
  Upload,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Axios from "axios";
import { applyPatch } from "fast-json-patch";
import ReactJson from "react18-json-view";
import { v4 as uuidv4 } from "uuid";

import "react18-json-view/src/style.css";

import { useSnackbar } from "notistack";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

import { UsersApiHelperForMember } from "../../api/ApiHelper";
import { getDocumentStructuredData } from "../../api-toolkit/FilesHelper";
import { AmplifySessionManager } from "../../api/AmplifySessionManager";
import { AppContext } from "../../App";
import { MODEL_KEY_REASONING_FAST } from "../../config/modelConstants";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { USER_ROLE_ADMIN } from "../../core/useSession";
import { formatDateContext, formatNumberFinancial } from "../../utility/formatters";
import { HeyTilleyIcon, MicrophoneIcon, SendIcon } from "../Icons/Icons";
import { isChatAssistantExportSupported, runChatAssistantExport } from "../Vault/Farm/components/Export/ExportService";
import { importTransformedDocument, isChatAssistantImportSupported } from "../Vault/Farm/components/Import/ImportService";
import ChunkImageDialog from "./ChunkImageDialog";
import {
  DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY,
  DOCUMENT_FORM_NAME_ARTICLES_OF_INCORPORATION,
  DOCUMENT_FORM_NAME_BUDGET,
  DOCUMENT_FORM_NAME_BUDGET_CROPS,
  DOCUMENT_FORM_NAME_COTTON_GIN_SHEET,
  DOCUMENT_FORM_NAME_DRIVERS_LICENSE,
  DOCUMENT_FORM_NAME_FARMSFSA578FORM,
  DOCUMENT_FORM_NAME_INSURANCE_SCHEDULE,
  DOCUMENT_FORM_NAME_LOAN_ORIGINATION_MEMO,
  DOCUMENT_FORM_NAME_RECEIPT_EQUIPMENT,
  DOCUMENT_FORM_NAME_RECEIPT_INPUTS,
  DOCUMENT_FORM_NAME_TAX_RETURN,
  DOCUMENT_FORM_NAME_UTILITY_BILL,
} from "./FinancialHelpers";
import JsonDocumentViewer from "./JsonDocumentViewer";

const FORM_NAMES_SUPPORTED = [
  DOCUMENT_FORM_NAME_FARMSFSA578FORM,
  DOCUMENT_FORM_NAME_BASE_ACRES,
  DOCUMENT_FORM_NAME_BALANCE_SHEET,
  DOCUMENT_FORM_NAME_INCOME_STATEMENT,
  DOCUMENT_FORM_NAME_SCHEDULE_F,
  DOCUMENT_FORM_NAME_EQUIPMENT_INVENTORY,
  DOCUMENT_FORM_NAME_DEBT_SCHEDULE,
  DOCUMENT_FORM_NAME_LOAN_ORIGINATION_MEMO,
  DOCUMENT_FORM_NAME_INSURANCE_SCHEDULE,
  DOCUMENT_FORM_NAME_ACTUAL_PRODUCTION_HISTORY,
  DOCUMENT_FORM_NAME_TAX_RETURN,
  DOCUMENT_FORM_NAME_DRIVERS_LICENSE,
  DOCUMENT_FORM_NAME_ARTICLES_OF_INCORPORATION,
  DOCUMENT_FORM_NAME_UTILITY_BILL,
  DOCUMENT_FORM_NAME_BUDGET,
  DOCUMENT_FORM_NAME_BUDGET_CROPS,
  DOCUMENT_FORM_NAME_RECEIPT_INPUTS,
  DOCUMENT_FORM_NAME_RECEIPT_EQUIPMENT,
  DOCUMENT_FORM_NAME_COTTON_GIN_SHEET,
];

export const DOCUMENT_BUDGET_CROPS = "BudgetCrops";
export const DOCUMENT_RECEIPT_INPUTS = "ReceiptInputs";
export const DOCUMENT_RECEIPT_EQUIPMENT = "ReceiptEquipment";
export const DOCUMENT_COTTON_GIN_SHEET = "CottonGinSheet";
export const DOCUMENT_BUDGET = "Budget";
export const DOCUMENT_ACTUAL_PRODUCTION_HISTORY = "ActualProductionHistory";
export const DOCUMENT_BALANCE_SHEET = "BalanceSheet";
export const DOCUMENT_INCOME_STATEMENT = "IncomeStatement";
export const DOCUMENT_DEBT_SCHEDULE = "DebtSchedule";
export const DOCUMENT_EQUIPMENT_INVENTORY = "EquipmentInventory";

export const DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH = "document_search";
export const DOCUMENT_SYSTEM_PROMPT_NAME_EDIT = "document_edit";

export const DOCUMENT_SEARCH_RESULTS_LIMIT = 20;

export const TASK_DOCUMENT_EXTRACT = "document_extract";

/**
 * UsersApiHelperForMember.callPost runs @datanac/datanac-api-toolkit normalizeResponse on array bodies,
 * which lowercases every key on each row (isProcessed→isprocessed, createDate→createdate, etc.).
 * Task/search returns an array → fields must be read with fallbacks. Task/enqueue returns a single object → unchanged camelCase.
 */
const taskProcessed = (t) => !!(t?.isProcessed ?? t?.isprocessed);
const taskCreateDate = (t) => t?.createDate ?? t?.createdate;
const taskEndDate = (t) => t?.endDate ?? t?.enddate;
const taskResponse = (t) => t?.response ?? t?.Response;
const taskNameOf = (t) => t?.taskName ?? t?.taskname;

export const DocumentChatAssistant = ({ documents, currentYear }) => {
  const { globalState } = useContext(AppContext);
  const timerRef = useRef(null);
  const theme = useTheme();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showHelper, setShowHelper] = useState(false);
  const [showButton, setShowButton] = useState(true);
  const { attachments: contextAttachments } = useChatAssistantAttachments();
  const { enqueueSnackbar } = useSnackbar();

  const [inputMessage, setInputMessage] = useState("");
  const [assistantResponse, setAssistantResponse] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isWorking, setIsWorking] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [mode, setMode] = useState(DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH); // "search" or "edit"

  // Filter states
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [selectedDocumentFileName, setSelectedDocumentFileName] = useState("");

  // Document preview and contents states
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState(null);
  const [documentContents, setDocumentContents] = useState([]); // Memory/Item search results for table view
  const [documentStructuredJson, setDocumentStructuredJson] = useState(null); // Files/File structured data for JSON view
  const [originalDocumentJson, setOriginalDocumentJson] = useState(null); // Original JSON from web for reset functionality
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLoadingContents, setIsLoadingContents] = useState(false);
  const [isLoadingStructuredData, setIsLoadingStructuredData] = useState(false);
  const [contentsViewMode, setContentsViewMode] = useState("table"); // "table" or "json"
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [documentMetadataCache, setDocumentMetadataCache] = useState({});
  const [documentTasks, setDocumentTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [isEnqueuingTask, setIsEnqueuingTask] = useState(false);
  const pollingIntervalRef = useRef(null);

  // Constants
  const TASK_STATUS_DISPLAY_MAX = 5;
  const TASK_SEARCH_TIME_THRESHOLD_MINUTES = 5;
  const pollingTimeoutRef = useRef(null);

  const { listening, transcript, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  // API function to search Memory/Item
  const searchDocumentMemory = async (searchText, documentIdOverride = null) => {
    try {
      const params = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token || globalState?.agent_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        memoryName: "Documents",
        // producer_token + agent_token are required for the UsersAPI relay's
        // agent->producer UserGrant authorization.
        producer_token: globalState?.producer_token,
        agent_token: globalState?.agent_token,
      };

      if (searchText !== null && searchText !== undefined && searchText !== "") {
        params.searchText = searchText;
      }

      const docId = documentIdOverride || selectedDocumentId;
      if (selectedDocumentType && !docId) {
        params.filterAttribute = "documentType";
        params.filterValue = selectedDocumentType;
      } else if (docId) {
        params.filterDocumentId = docId;
      }

      const response = await UsersApiHelperForMember.http_get(
        `${import.meta.env.VITE_DATANAC_USERS_API_URL_BASE}/Memory/Item`,
        params
      );

      // Axios usually parses JSON, but the legacy RG endpoint occasionally
      // returns the JSON array preceded by log/text lines, so accept both.
      const raw = response?.data;
      if (Array.isArray(raw)) {
        return raw;
      }
      if (typeof raw === "string") {
        const lines = raw.split("\n");
        const startIdx = lines.findIndex((line) => line.trim().startsWith("["));
        if (startIdx === -1) {
          console.warn("No JSON array found in Memory/Item response");
          return [];
        }
        try {
          return JSON.parse(lines.slice(startIdx).join("\n")) || [];
        } catch (parseErr) {
          console.warn("Failed to parse Memory/Item response:", parseErr);
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error("Error searching document memory:", error);
      return [];
    }
  };

  // Helper function to sort document contents by _rg_ordinal if it exists
  const sortDocumentContents = (contents) => {
    if (!contents || contents.length === 0) return contents;

    // Check if _rg_ordinal exists in the first item
    const hasOrdinal = contents.some((item) => item._rg_ordinal !== undefined && item._rg_ordinal !== null);

    if (hasOrdinal) {
      return [...contents].sort((a, b) => {
        const ordinalA = Number(a._rg_ordinal) || 0;
        const ordinalB = Number(b._rg_ordinal) || 0;
        return ordinalA - ordinalB;
      });
    }

    return contents;
  };

  // Function to search for tasks by document_id
  const searchDocumentTasks = async (docId) => {
    if (!docId) {
      return [];
    }

    try {
      const requestBody = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token || globalState?.agent_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        properties: {
          producer_token: globalState?.producer_token,
          agent_token: globalState?.agent_token,
          document_id: docId,
        },
      };

      const tasks = await UsersApiHelperForMember.callPost("Task/search", requestBody, globalState);
      const allTasks = Array.isArray(tasks) ? tasks : [];

      // Group tasks by ID to check completion status
      const taskMap = new Map();
      allTasks.forEach((task) => {
        const taskId = task.id ?? task.Id;
        if (!taskMap.has(taskId)) {
          taskMap.set(taskId, []);
        }
        taskMap.get(taskId).push(task);
      });

      // Filter task groups: keep all status updates for tasks that are active or recently completed
      const now = new Date();
      const filteredTasks = [];

      taskMap.forEach((taskUpdates) => {
        // Sort updates by createDate descending to get the most recent status
        const sortedUpdates = taskUpdates.sort((a, b) => new Date(taskCreateDate(b)) - new Date(taskCreateDate(a)));
        const mostRecentUpdate = sortedUpdates[0];

        // Show all updates if task is not yet processed
        if (!taskProcessed(mostRecentUpdate)) {
          filteredTasks.push(...sortedUpdates);
          return;
        }

        // Show all updates if task was completed recently (within TASK_SEARCH_TIME_THRESHOLD_MINUTES)
        const completionDate = taskEndDate(mostRecentUpdate) || taskCreateDate(mostRecentUpdate);
        if (completionDate) {
          const completed = new Date(completionDate);
          const minutesSinceCompletion = (now - completed) / (1000 * 60);
          if (minutesSinceCompletion <= TASK_SEARCH_TIME_THRESHOLD_MINUTES) {
            filteredTasks.push(...sortedUpdates);
          }
        }
      });

      return filteredTasks;
    } catch (error) {
      console.error("Error searching document tasks:", error);
      return [];
    }
  };

  // Function to load tasks for a document with optional silent mode
  const loadDocumentTasks = async (docId, silent = false) => {
    if (!docId) {
      setDocumentTasks([]);
      return;
    }

    if (!silent) {
      setIsLoadingTasks(true);
    }

    try {
      const tasks = await searchDocumentTasks(docId);
      setDocumentTasks(tasks);

      // Return object with tasks info
      return {
        allProcessed: tasks.length > 0 && tasks.every((task) => taskProcessed(task)),
        taskCount: tasks.length,
      };
    } catch (error) {
      console.error("Error loading document tasks:", error);
      setDocumentTasks([]);
      return { allProcessed: true, taskCount: 0 }; // Stop polling on error
    } finally {
      if (!silent) {
        setIsLoadingTasks(false);
      }
    }
  };

  // Function to enqueue a document extract task
  const enqueueDocumentExtractTask = async (docId, docName) => {
    if (!docId) {
      return;
    }

    setIsEnqueuingTask(true);

    try {
      const requestBody = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token || globalState?.agent_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        taskName: TASK_DOCUMENT_EXTRACT,
        promptName: null,
        userMessage: `Original filename = "${docName}"`,
        media: [],
        properties: {
          producer_token: globalState?.producer_token,
          agent_token: globalState?.agent_token,
          document_id: docId,
        },
      };

      await UsersApiHelperForMember.callPost("Task/enqueue", requestBody, globalState);

      // Reload tasks to trigger polling
      await loadDocumentTasks(docId);
    } catch (error) {
      console.error("Error enqueueing document extract task:", error);
      enqueueSnackbar("Failed to start document extraction", { variant: "error" });
    } finally {
      setIsEnqueuingTask(false);
    }
  };

  // Function to load document contents with searchText="*"
  const loadDocumentContents = async (docId) => {
    if (!docId) {
      setDocumentContents([]);
      return;
    }

    setIsLoadingContents(true);
    try {
      const contents = await searchDocumentMemory(null, docId);
      const sortedContents = sortDocumentContents(contents);
      setDocumentContents(sortedContents);
    } catch (error) {
      console.error("Error loading document contents:", error);
      setDocumentContents([]);
    } finally {
      setIsLoadingContents(false);
    }
  };

  // Function to load structured document data from Files/File endpoint
  const loadDocumentStructuredData = async (docId) => {
    if (!docId) {
      setDocumentStructuredJson(null);
      setOriginalDocumentJson(null);
      return;
    }

    setIsLoadingStructuredData(true);
    try {
      const structuredData = await getDocumentStructuredData(docId, false, globalState);
      setDocumentStructuredJson(structuredData);
      // Store a deep copy of the original data for reset functionality
      setOriginalDocumentJson(structuredData ? JSON.parse(JSON.stringify(structuredData)) : null);
    } catch (error) {
      console.error("Error loading document structured data:", error);
      setDocumentStructuredJson(null);
      setOriginalDocumentJson(null);
    } finally {
      setIsLoadingStructuredData(false);
    }
  };

  // Function to load document preview URL
  const loadDocumentPreview = async (docId) => {
    if (!docId) {
      setDocumentPreviewUrl(null);
      return;
    }

    const selectedDoc = documents?.find((doc) => doc.id === docId);
    if (!selectedDoc) {
      setDocumentPreviewUrl(null);
      return;
    }

    setIsLoadingPreview(true);
    try {
      // Get the document URL from RG Files API
      const structuredData = await getDocumentStructuredData(docId, false, globalState);
      if (structuredData?.metadata?.documentUrl) {
        // Fetch PDF as blob to override incorrect Content-Type from S3
        const response = await Axios.get(structuredData.metadata.documentUrl, {
          responseType: "blob",
        });

        // Create blob with explicit PDF type to fix Content-Type issue
        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(pdfBlob);

        setDocumentPreviewUrl(blobUrl);
      } else {
        console.warn("No documentUrl found in metadata for document:", docId);
        setDocumentPreviewUrl(null);
      }
    } catch (error) {
      console.error("Error loading document preview:", error);
      setDocumentPreviewUrl(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  /**
   * Transform JSON content for import by:
   * 1. Adding producer_token to root
   * 2. For each array property, add producer_token and ensure id exists on each item
   */
  const transformJsonForImport = (jsonContent, producerToken) => {
    if (!jsonContent || typeof jsonContent !== "object") {
      return jsonContent;
    }

    const transformed = {
      producer_token: producerToken,
    };

    // Loop through all properties in the JSON content
    Object.keys(jsonContent).forEach((key) => {
      const value = jsonContent[key];

      // If the value is an array, transform each item
      if (Array.isArray(value)) {
        transformed[key] = value.map((item) => {
          if (typeof item !== "object" || item === null) {
            return item;
          }

          // Ensure the item has an id property
          const hasId = item.id || item.Id;

          return {
            ...item,
            // Add id if it doesn't exist (check both lowercase and uppercase)
            ...(hasId ? {} : { id: uuidv4() }),
            producer_token: producerToken,
          };
        });
      } else {
        // For non-array values, just copy them
        transformed[key] = value;
      }
    });

    return transformed;
  };

  /**
   * Extract the data array from transformed content - generic approach
   * Finds the first array property in the JSON (excluding producer_token)
   */
  const extractDocumentDataArray = (transformedContent) => {
    if (!transformedContent || typeof transformedContent !== "object") {
      return null;
    }

    // Find the first property that is an array (skip producer_token and other metadata)
    for (const key of Object.keys(transformedContent)) {
      if (key === "producer_token" || key === "agent_token") {
        continue;
      }

      const value = transformedContent[key];
      if (Array.isArray(value) && value.length > 0) {
        return value;
      }
    }

    return null;
  };

  // Handler for import button click
  const handleImportClick = async (documentType) => {
    setIsImporting(true);

    try {
      // Use documentStructuredJson.content instead of documentContents
      if (!documentStructuredJson?.content) {
        enqueueSnackbar("No document content to import", { variant: "error" });
        setIsImporting(false);
        return;
      }

      // Transform the JSON content to add producer_token and ensure id exists
      const transformedContent = transformJsonForImport(documentStructuredJson.content, globalState.producer_token);

      // Extract the array data from the transformed JSON (generic - finds first array)
      const documentData = extractDocumentDataArray(transformedContent);

      if (!documentData || documentData.length === 0) {
        enqueueSnackbar("No data array found in document content", { variant: "error" });
        setIsImporting(false);
        return;
      }

      const result = await importTransformedDocument(documentType, documentData, selectedDocumentId, globalState, currentYear);

      if (!(result.success || result.isInfo)) {
        enqueueSnackbar(result.message, { variant: "error" });
      }
    } catch (error) {
      console.error("Error importing document:", error);
      enqueueSnackbar("Failed to import document", { variant: "error" });
    }

    setIsImporting(false);
  };

  const handleExportClick = async () => {
    setIsExporting(true);
    try {
      const result = await runChatAssistantExport(selectedDocumentType, globalState, currentYear, enqueueSnackbar, {
        structuredContent: documentStructuredJson?.content ?? null,
        exportOptions: {},
      });
      if (!result.success && result.message) {
        enqueueSnackbar(result.message, { variant: result.variant || "error" });
      }
    } catch (error) {
      console.error("Error exporting document:", error);
      enqueueSnackbar(error?.message || "Failed to export", { variant: "error" });
    }
    setIsExporting(false);
  };

  // Handler for save button click
  const handleSaveClick = async () => {
    setIsSaving(true);

    try {
      if (!documentStructuredJson?.content) {
        enqueueSnackbar("No document content to save", { variant: "error" });
        setIsSaving(false);
        return;
      }

      // Get a writable contentUrl from the Files API
      const writableData = await getDocumentStructuredData(selectedDocumentId, true, globalState);
      const writableContentUrl = writableData?.metadata?.contentUrl;

      if (!writableContentUrl) {
        enqueueSnackbar("No writable content URL found for this document", { variant: "error" });
        setIsSaving(false);
        return;
      }

      // Upload the updated JSON content to the writable contentUrl
      await Axios.put(writableContentUrl, documentStructuredJson.content, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Update the original JSON to reflect the newly saved state
      setOriginalDocumentJson(JSON.parse(JSON.stringify(documentStructuredJson)));
    } catch (error) {
      console.error("Error saving document content:", error);
      enqueueSnackbar("Failed to save document content", { variant: "error" });
    }

    setIsSaving(false);
  };

  // Handler for cancel button click - resets to original JSON from web
  const handleCancelClick = () => {
    if (originalDocumentJson) {
      // Reset to the original JSON from the web
      setDocumentStructuredJson(JSON.parse(JSON.stringify(originalDocumentJson)));
    }
  };

  const handleImageDialogOpen = (imageUrl) => {
    // Find the index of this image in the chunkUrls array
    const chunkUrls = documentMetadataCache[selectedDocumentId]?.chunkUrls || [];
    const index = chunkUrls.indexOf(imageUrl);
    setCurrentImageIndex(index >= 0 ? index : 0);
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
  };

  const handleIndexChange = (newIndex) => {
    setCurrentImageIndex(newIndex);
  };

  // Fetch and cache document metadata
  const getDocumentMetadata = useCallback(
    async (docId) => {
      // Return cached metadata if available - use functional state update to avoid dependency
      let cachedValue;
      setDocumentMetadataCache((prev) => {
        cachedValue = prev[docId];
        return prev;
      });

      if (cachedValue) {
        return cachedValue;
      }

      try {
        const structuredData = await getDocumentStructuredData(docId, false, globalState);
        const metadata = structuredData?.metadata || null;

        // Cache the metadata
        setDocumentMetadataCache((prev) => ({
          ...prev,
          [docId]: metadata,
        }));

        return metadata;
      } catch (error) {
        console.error("Error fetching document metadata:", error);
        return null;
      }
    },
    [globalState]
  );

  useEffect(() => {
    if (!browserSupportsSpeechRecognition) {
      alert("Browser doesn't support speech recognition");
    }
  }, [browserSupportsSpeechRecognition]);

  useEffect(() => {
    if (listening) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setElapsedTime(0);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [listening]);

  useEffect(() => {
    if (elapsedTime >= 30 && listening) {
      handleStopRecording();
      return;
    }
  }, [elapsedTime, listening]);

  const handleStartRecording = async () => {
    resetTranscript();
    SpeechRecognition.startListening({ continuous: true });
  };

  const handleStopRecording = async () => {
    SpeechRecognition.stopListening();
  };

  // Update input from transcript
  useEffect(() => {
    if (transcript && !listening) {
      setInputMessage(transcript);
    }
  }, [transcript, listening]);

  const sendChatCompletion = async () => {
    if (!inputMessage.trim() || isWorking) return;

    setIsWorking(true);
    setAssistantResponse("");
    setSearchResults([]);

    try {
      // Step 1: Prepare messages and attachments
      let messages;
      let allAttachments;

      const systemAttachments = [
        {
          name: "System Information",
          content: `Current Date = ${new Date().toLocaleString()}`,
          doPreload: true,
        },
      ];

      if (selectedDocumentId && documentStructuredJson?.content) {
        // Document is selected - include JSON content as a separate user message PRIOR to the user's request (no Memory/Item API search, no attachments)
        messages = [{ role: "user", content: inputMessage }];
        const documentAttachments = [
          {
            name: "DOCUMENT_DATA",
            content: documentStructuredJson.content,
            doPreload: true,
          },
        ];
        allAttachments = [...systemAttachments, ...documentAttachments];
      } else {
        // Global search - search document memory using Memory/Item API and include as attachment
        const memorySearchResults = await searchDocumentMemory(inputMessage);
        setSearchResults(memorySearchResults); // Store separately for UI table display

        messages = [{ role: "user", content: inputMessage }];
        const documentAttachments = [
          {
            name: "DOCUMENT_DATA",
            content: memorySearchResults,
            doPreload: true,
          },
        ];
        allAttachments = [...systemAttachments, ...contextAttachments, ...documentAttachments];
      }

      // Step 2: Send chat completion using /v1/responses API
      const request = {
        model: mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT ? MODEL_KEY_REASONING_FAST : null,
        messages: messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        })),
        assistant:
          mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT
            ? DOCUMENT_SYSTEM_PROMPT_NAME_EDIT
            : DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH,
        stream: mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT ? false : true,
        temperature: 0.7,
        attachments: allAttachments.map((a) => ({
          name: a.name,
          content: typeof a.content === "string" ? a.content : JSON.stringify(a.content),
          doPreload: a.doPreload || false,
        })),
        reasoning: {
          effort: "low",
        },
      };

      const sessionManager = new AmplifySessionManager();
      const jwt = await sessionManager.getBearerToken();

      const response = await fetch(import.meta.env.VITE_RGCHAT_API_URL_ROOT + `v1/responses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + jwt,
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      let cumulativeResult = "";

      // Handle response based on streaming mode
      if (mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT) {
        // Non-streaming response: parse as complete JSON
        const responseData = await response.json();
        cumulativeResult = responseData.choices?.[0]?.message?.content || "";
      } else {
        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6).trim();

              if (data === "[DONE]") {
                break;
              }

              try {
                const parsed = JSON.parse(data);
                const delta = parsed.choices?.[0]?.delta;

                if (delta?.content) {
                  cumulativeResult += delta.content;

                  // Heuristic: Check if response looks like JSON
                  const trimmedResult = cumulativeResult.trim();
                  const looksLikeJson =
                    (trimmedResult.startsWith("{") || trimmedResult.startsWith("[")) &&
                    selectedDocumentId &&
                    documentStructuredJson?.content;

                  // Only update UI during streaming if it doesn't look like JSON
                  // (If it's JSON, we'll process it after streaming completes)
                  if (!looksLikeJson) {
                    setAssistantResponse(cumulativeResult);
                  }
                }
              } catch (e) {
                console.error("Failed to parse SSE data:", e);
              }
            }
          }
        }
      }

      // If in edit mode with a document selected and we have structured JSON, try to apply JSON patch
      if (mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT && selectedDocumentId && documentStructuredJson?.content) {
        try {
          // Try to parse the response as JSON patch
          const parsed = JSON.parse(cumulativeResult);
          // Extract the document field if it exists (wrapper format), otherwise use parsed directly
          const jsonPatch = parsed.document || parsed;

          // Check if it's a valid JSON patch array
          if (Array.isArray(jsonPatch) && jsonPatch.length > 0 && jsonPatch[0].op) {
            // Clone the current document content
            let documentToUpdate = JSON.parse(JSON.stringify(documentStructuredJson.content));

            // Sort patches in descending order by path to avoid index shifting issues
            // when removing multiple items from arrays - parse path segments numerically
            const sortedPatch = [...jsonPatch].sort((a, b) => {
              const pathA = a.path || "";
              const pathB = b.path || "";

              // Split paths and compare segment by segment
              const segmentsA = pathA.split("/").filter((s) => s);
              const segmentsB = pathB.split("/").filter((s) => s);

              for (let i = 0; i < Math.max(segmentsA.length, segmentsB.length); i++) {
                const segA = segmentsA[i] || "";
                const segB = segmentsB[i] || "";

                // Try to parse as numbers
                const numA = parseInt(segA, 10);
                const numB = parseInt(segB, 10);

                // If both are numbers, compare numerically (descending)
                if (!isNaN(numA) && !isNaN(numB)) {
                  if (numB !== numA) return numB - numA;
                } else {
                  // Otherwise compare as strings (descending)
                  if (segB !== segA) return segB.localeCompare(segA);
                }
              }

              return 0;
            });

            // Apply each patch operation individually
            for (const operation of sortedPatch) {
              try {
                // Normalize path by removing trailing slash if present (e.g., "/path/0/" -> "/path/0")
                if (operation.path && operation.path.endsWith("/") && operation.path.length > 1) {
                  operation.path = operation.path.slice(0, -1);
                }

                const patchResult = applyPatch(documentToUpdate, [operation], true, false);
                documentToUpdate = patchResult.newDocument;
              } catch (error) {
                console.warn("JSON Patch Error:", error, operation);
                handleError("Error occurred while updating document.");
              }
            }

            // Update the document structured JSON state with patched content
            setDocumentStructuredJson({
              ...documentStructuredJson,
              content: documentToUpdate,
            });
          } else {
            // Not a JSON patch, just set as regular response
            setAssistantResponse(cumulativeResult);
          }
        } catch (patchError) {
          console.warn("JSON Patch Error (2)", patchError);
          handleError("Error occurred while updating document.");
          // If parsing or patching fails, just set as regular response
          setAssistantResponse(cumulativeResult);
        }
      } else {
        // No document selected, just set as regular response
        setAssistantResponse(cumulativeResult);
      }

      setInputMessage("");
      resetTranscript();
    } catch (error) {
      console.error("Chat completion error:", error);
      setAssistantResponse("Sorry, there was an error processing your request.");
    } finally {
      setIsWorking(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendChatCompletion();
    }
  };

  const toggleHelper = () => {
    if (showHelper) {
      setShowHelper(false);
    } else {
      setShowButton(false);
      setShowHelper(true);
    }
  };

  const handleCollapseExited = () => {
    setShowButton(true);
  };

  const handleDocumentTypeChange = (event) => {
    setSelectedDocumentType(event.target.value);
    // Clear document selection when type changes
    setSelectedDocumentId("");
    setSelectedDocumentFileName("");
  };

  // Load document preview, contents, and structured data when document is selected
  useEffect(() => {
    if (selectedDocumentId) {
      loadDocumentPreview(selectedDocumentId);
      loadDocumentContents(selectedDocumentId);
      loadDocumentStructuredData(selectedDocumentId);
      getDocumentMetadata(selectedDocumentId);
      loadDocumentTasks(selectedDocumentId);
      // Reset chat state when document changes
      setAssistantResponse("");
      setSearchResults([]);
      setInputMessage("");
    } else {
      setDocumentPreviewUrl(null);
      setDocumentContents([]);
      setDocumentStructuredJson(null);
      setOriginalDocumentJson(null);
      setDocumentTasks([]);
      // Reset chat state when clearing document
      setAssistantResponse("");
      setSearchResults([]);
      setInputMessage("");
    }
  }, [selectedDocumentId]);

  // Reload document data when all tasks complete
  useEffect(() => {
    if (!selectedDocumentId || documentTasks.length === 0) {
      return;
    }

    const allTasksProcessed = documentTasks.every((task) => taskProcessed(task));
    if (allTasksProcessed) {
      // Reload document data after extraction completes
      loadDocumentStructuredData(selectedDocumentId);
      loadDocumentContents(selectedDocumentId);
    }
  }, [documentTasks, selectedDocumentId]);

  // Polling for task updates with exponential backoff
  useEffect(() => {
    // Clear any existing polling timers
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Only poll if document is selected and has tasks
    if (!selectedDocumentId || documentTasks.length === 0) {
      return;
    }

    // Check if all tasks are processed
    const allTasksProcessed = documentTasks.every((task) => taskProcessed(task));
    if (allTasksProcessed) {
      return;
    }

    // Polling configuration with exponential backoff
    let pollCount = 0;
    const maxPollCount = 30; // Stop after ~5 minutes (2s + 4s + 8s + 16s + 16s...)
    const baseInterval = 2000; // Start with 2 seconds
    const maxInterval = 16000; // Cap at 16 seconds
    let hadTasksBefore = documentTasks.length > 0;

    const scheduleNextPoll = () => {
      if (pollCount >= maxPollCount) {
        return;
      }

      // Calculate next interval with exponential backoff
      const nextInterval = Math.min(baseInterval * Math.pow(2, pollCount), maxInterval);
      pollCount++;

      pollingTimeoutRef.current = setTimeout(async () => {
        const result = await loadDocumentTasks(selectedDocumentId, true);

        // Check if tasks suddenly disappeared (no response = tasks completed)
        if (hadTasksBefore && result.taskCount === 0) {
          // Reload all document data (except PDF preview)
          loadDocumentContents(selectedDocumentId);
          loadDocumentStructuredData(selectedDocumentId);
          getDocumentMetadata(selectedDocumentId);
        }

        if (!result.allProcessed) {
          hadTasksBefore = result.taskCount > 0;
          scheduleNextPoll();
        }
      }, nextInterval);
    };

    // Start polling
    scheduleNextPoll();

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [selectedDocumentId, documentTasks]);

  // Cleanup blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (documentPreviewUrl && documentPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(documentPreviewUrl);
      }
    };
  }, [documentPreviewUrl]);

  // Fetch metadata for all unique document IDs in search results
  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      // Get unique document IDs
      const uniqueDocIds = [
        ...new Set(
          searchResults.map((result) => result.documentId || result.DocumentId).filter((id) => id && id !== "-")
        ),
      ];

      // Fetch metadata for each unique document ID
      uniqueDocIds.forEach((docId) => {
        getDocumentMetadata(docId);
      });
    }
  }, [searchResults]);

  // Filter documents by selected type (PDF only for now)
  const pdfDocuments = documents?.filter((doc) => doc.content_type?.toLowerCase().includes("pdf")) || [];

  const filteredDocuments = selectedDocumentType
    ? pdfDocuments.filter((doc) => doc.document_type_name === selectedDocumentType)
    : pdfDocuments;

  // Memoize the JSON view to prevent unnecessary re-renders
  const jsonView = useMemo(() => {
    if (contentsViewMode !== "json" || !documentStructuredJson?.content) {
      return null;
    }
    return (
      <ReactJson
        src={documentStructuredJson.content}
        theme={theme.palette.mode === "dark" ? "atom" : "default"}
        collapsed={false}
        displayDataTypes={false}
        displayObjectSize={true}
        enableClipboard={true}
        style={{ fontSize: "12px" }}
      />
    );
  }, [documentStructuredJson, contentsViewMode]);

  const getDocumentLabel = (option) => {
    return [option.document_type_name, option.file_name, option.id?.substring(0, 8)].filter(Boolean).join(" - ");
  };

  return (
    <Box className={!showHelper ? "chat_assistant_button" : isMobile ? "mobile_chat_assistant_button" : ""}>
      {showButton && !showHelper && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
            <IconButton
              onClick={toggleHelper}
              sx={{
                bgcolor: "primary.main",
                color: "primary.contrastText",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                width: "48px",
                height: "48px",
                position: "relative",
                "[data-mui-color-scheme='dark'] &": {
                  bgcolor: "primary.dark",
                  color: "primary.contrastText",
                },
                "[data-mui-color-scheme='dark'] &:hover": {
                  bgcolor: "primary.main",
                },
              }}
            >
              {listening ? (
                <RadioButtonChecked className="pulsating" sx={{ color: "white" }} />
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M4 9.77778V14.2222M8 6.44444V17.5556M12 2V22M16 9.77778V14.2222M20 6.44444V17.5556"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              <Box
                sx={{
                  position: "absolute",
                  right: "-4px",
                  top: "-4px",
                }}
              >
                <HeyTilleyIcon sx={{ width: "28px", height: "28px" }} color="secondary" />
              </Box>
            </IconButton>
          </Box>
        </Box>
      )}

      <Collapse in={showHelper} timeout="auto" unmountOnExit onExited={handleCollapseExited}>
        <Box
          sx={{
            display: { xs: "block", md: "inline-block" },
            width: { xs: "100%", md: "100%" },
            mt: { xs: 2, md: 0 },
            px: { xs: 0, sm: 0 },
          }}
        >
          {/* Filters - Always show at top */}
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 200, width: "100%", maxWidth: { xs: "100%", md: 300 } }} size="small">
              <InputLabel id="document-type-filter-label">Document Type</InputLabel>
              <Select
                labelId="document-type-filter-label"
                id="document-type-filter"
                value={selectedDocumentType}
                label="Document Type"
                onChange={handleDocumentTypeChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>All Types</em>
                </MenuItem>
                {FORM_NAMES_SUPPORTED.map((formName) => (
                  <MenuItem key={formName} value={formName}>
                    {formName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ minWidth: 200, width: "100%", maxWidth: { xs: "100%", md: 300 } }}
              size="small"
              disabled={!documents || documents.length === 0}
              options={[{ id: "", document_type_name: "", document_name: "", isAll: true }, ...filteredDocuments]}
              getOptionLabel={(option) => (option.isAll ? "All Documents" : getDocumentLabel(option))}
              value={selectedDocumentId ? filteredDocuments.find((doc) => doc.id === selectedDocumentId) || null : null}
              onChange={(event, newValue) => {
                if (newValue === null || newValue.isAll) {
                  setSelectedDocumentId("");
                  setSelectedDocumentFileName("");
                } else {
                  setSelectedDocumentId(newValue.id);
                  setSelectedDocumentFileName(newValue.file_name);
                  // Also set the document type based on the selected document
                  if (newValue?.document_type_name) {
                    setSelectedDocumentType(newValue.document_type_name);
                  }
                }
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                    }}
                  >
                    {option.isAll ? "All Documents" : getDocumentLabel(option)}
                  </Typography>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Document" />}
              clearOnEscape
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Stack>

          {/* Document-focused view when a specific document is selected */}
          {selectedDocumentId ? (
            <Grid container spacing={2}>
              {/* Preview Pane - Just the iframe viewer */}
              <Grid item xs={12} md={3}>
                <Box
                  sx={{
                    height: "700px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.100",
                  }}
                >
                  {isLoadingPreview ? (
                    <CircularProgress />
                  ) : documentPreviewUrl ? (
                    <iframe
                      src={documentPreviewUrl}
                      type="application/pdf"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        backgroundColor: "white",
                      }}
                      title="Document Preview"
                      onLoad={(e) => {}}
                      onError={(e) => {
                        console.error("Iframe failed to load");
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No preview available
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Contents Pane - Shows document contents from Memory API */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    height: "700px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 600 }}>
                      Document Contents {documentContents.length > 0 && `(${documentContents.length} items)`}
                    </Typography>
                    {/* View Toggle */}
                    <Box
                      sx={{
                        position: "relative",
                        width: 100,
                        height: 50,
                        backgroundColor: "transparent",
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "4px",
                        boxSizing: "border-box",
                        border: "2px solid var(--mui-palette-primary-main)",
                        "[data-mui-color-scheme='dark'] &": {
                          border: "2px solid var(--mui-palette-primary-light)",
                        },
                      }}
                    >
                      {/* Sliding background */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 2,
                          left: contentsViewMode === "table" ? 2 : "calc(50% + 3px)",
                          width: "calc(50% - 5px)",
                          height: "calc(100% - 4px)",
                          borderRadius: "999px",
                          transition: "left 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                          zIndex: 1,
                          backgroundColor: "primary.main",
                          "[data-mui-color-scheme='dark'] &": {
                            backgroundColor: "primary.light",
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          position: "relative",
                          zIndex: 2,
                        }}
                      >
                        <IconButton
                          onClick={() => setContentsViewMode("table")}
                          aria-label="Switch to Table View"
                          sx={{ padding: "7px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <TableChart
                            sx={{
                              color: contentsViewMode === "table" ? "secondary.contrastText" : "primary.main",
                              transition: "color 0.3s ease-in-out",
                              "[data-mui-color-scheme='dark'] &": {
                                color: contentsViewMode === "table" ? "secondary.contrastText" : "primary.light",
                              },
                            }}
                          />
                        </IconButton>

                        <IconButton
                          onClick={() => setContentsViewMode("json")}
                          aria-label="Switch to JSON View"
                          sx={{ padding: "7px", display: "flex", alignItems: "center", justifyContent: "center" }}
                        >
                          <DataObject
                            sx={{
                              color: contentsViewMode === "json" ? "secondary.contrastText" : "primary.main",
                              transition: "color 0.3s ease-in-out",
                              "[data-mui-color-scheme='dark'] &": {
                                color: contentsViewMode === "json" ? "secondary.contrastText" : "primary.light",
                              },
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {/* Task Status Indicator */}
                  {isLoadingTasks ? (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">
                        Loading tasks...
                      </Typography>
                    </Box>
                  ) : documentTasks.length > 0 ? (
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                      {(() => {
                        // Check if the most recent task (sorted by createDate DESC) is complete
                        const sortedTasks = [...documentTasks].sort(
                          (a, b) => new Date(taskCreateDate(b)) - new Date(taskCreateDate(a))
                        );
                        const mostRecentTask = sortedTasks[0];
                        const isLatestComplete = taskProcessed(mostRecentTask);

                        return isLatestComplete ? (
                          <>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "success.main",
                                mt: 0.5,
                              }}
                            />
                            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                              Processing Complete
                            </Typography>
                          </>
                        ) : (
                          <>
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
                              Processing...
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  ) : null}
                  {/* Task Details Section - Activity Log */}
                  {documentTasks.length > 0 && (
                    <Box
                      sx={{
                        mb: 1,
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                    >
                      {documentTasks
                        .slice()
                        .sort((a, b) => new Date(taskCreateDate(b)) - new Date(taskCreateDate(a)))
                        .slice(0, TASK_STATUS_DISPLAY_MAX)
                        .map((task, index, filteredArray) => {
                          const isProc = taskProcessed(task);
                          const endDate = taskEndDate(task);
                          const createDate = taskCreateDate(task);
                          return (
                            <Box
                              key={(task.id ?? task.Id) || index}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                py: 0.5,
                                borderBottom: index < filteredArray.length - 1 ? "1px solid" : "none",
                                borderColor: "divider",
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: isProc ? "success.main" : "warning.main",
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontSize: "11px", flex: 1 }}>
                                  {taskResponse(task) || taskNameOf(task) || "Processing..."}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontSize: "10px", ml: 1, flexShrink: 0 }}
                              >
                                {isProc
                                  ? endDate
                                    ? `Completed ${new Date(endDate).toLocaleTimeString()}`
                                    : "Completed"
                                  : createDate
                                    ? formatDateContext(createDate)
                                    : "Pending"}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Box>
                  )}
                  <Box sx={{ flex: 1, overflow: "auto", mb: 1 }}>
                    {contentsViewMode === "table" ? (
                      isLoadingContents || isLoadingStructuredData ? (
                        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : documentStructuredJson?.content ? (
                        <JsonDocumentViewer
                          data={documentStructuredJson.content}
                          chunkUrls={documentMetadataCache[selectedDocumentId]?.chunkUrls || []}
                          documentId={selectedDocumentId}
                          globalState={globalState}
                        />
                      ) : documentTasks.length === 0 && !isLoadingTasks ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            gap: 2,
                          }}
                        >
                          <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center" }}>
                            No document content available
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => enqueueDocumentExtractTask(selectedDocumentId, selectedDocumentFileName)}
                            disabled={isEnqueuingTask}
                            sx={{
                              minWidth: 200,
                              minHeight: 60,
                              fontSize: "18px",
                              fontWeight: 600,
                            }}
                          >
                            {isEnqueuingTask ? "Starting Extraction..." : "Extract"}
                          </Button>
                        </Box>
                      ) : (
                        <Typography color="text.secondary" variant="body2" sx={{ p: 2 }}>
                          No contents found for this document
                        </Typography>
                      )
                    ) : isLoadingStructuredData || isLoadingContents ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : documentStructuredJson?.content ? (
                      jsonView
                    ) : documentTasks.length === 0 && !isLoadingTasks ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          gap: 2,
                        }}
                      >
                        <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center" }}>
                          No document content available
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          onClick={() => enqueueDocumentExtractTask(selectedDocumentId, selectedDocumentFileName)}
                          disabled={isEnqueuingTask}
                          sx={{
                            minWidth: 200,
                            minHeight: 60,
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        >
                          {isEnqueuingTask ? "Starting Extraction..." : "Extract"}
                        </Button>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" variant="body2" sx={{ p: 2 }}>
                        No structured content found for this document
                      </Typography>
                    )}
                  </Box>
                  {/* Import and Save Button Section - 64px height */}
                  <Box
                    sx={{
                      minHeight: "64px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                      borderTop: "1px solid",
                      borderColor: "divider",
                      pt: 1,
                      pb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Upload />}
                        onClick={() => handleImportClick(selectedDocumentType)}
                        disabled={
                          isImporting ||
                          !documentStructuredJson?.content ||
                          !isChatAssistantImportSupported(selectedDocumentType)
                        }
                        sx={{
                          minWidth: 120,
                        }}
                      >
                        {isImporting ? "Importing..." : "Import"}
                      </Button>
                      {isChatAssistantExportSupported(selectedDocumentType) && (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<Download />}
                          onClick={handleExportClick}
                          disabled={
                            isExporting || !globalState?.producer_token || currentYear == null || currentYear === ""
                          }
                          sx={{
                            minWidth: 120,
                          }}
                        >
                          {isExporting ? "Exporting..." : "Export"}
                        </Button>
                      )}
                      {globalState?.user_role === USER_ROLE_ADMIN && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => enqueueDocumentExtractTask(selectedDocumentId, selectedDocumentFileName)}
                          disabled={isEnqueuingTask}
                          sx={{
                            minWidth: 120,
                          }}
                        >
                          {isEnqueuingTask ? "Starting Extraction..." : "Extract"}
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<Cancel />}
                        onClick={handleCancelClick}
                        disabled={
                          !documentStructuredJson?.content ||
                          !originalDocumentJson?.content ||
                          !selectedDocumentId ||
                          JSON.stringify(documentStructuredJson) === JSON.stringify(originalDocumentJson)
                        }
                        sx={{
                          minWidth: 120,
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<Save />}
                        onClick={handleSaveClick}
                        disabled={
                          !documentStructuredJson?.content ||
                          !originalDocumentJson?.content ||
                          !selectedDocumentId ||
                          JSON.stringify(documentStructuredJson) === JSON.stringify(originalDocumentJson) ||
                          isSaving
                        }
                        sx={{
                          minWidth: 120,
                        }}
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Chat Interface - Focused and compact */}
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    height: "700px",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "grey.100",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 600 }}>
                      Document Assistant
                    </Typography>
                    {/* Mode Toggle - styled like WorkspaceModeSelector */}
                    <Box
                      sx={{
                        position: "relative",
                        width: 100,
                        height: 50,
                        backgroundColor: "transparent",
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "4px",
                        boxSizing: "border-box",
                        border: "2px solid var(--mui-palette-primary-main)",
                        "[data-mui-color-scheme='dark'] &": {
                          border: "2px solid var(--mui-palette-primary-light)",
                        },
                        opacity: isWorking ? 0.5 : 1,
                        pointerEvents: isWorking ? "none" : "auto",
                      }}
                    >
                      {/* Sliding background */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 2,
                          left: mode === DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH ? 2 : "calc(50% + 3px)",
                          width: "calc(50% - 5px)",
                          height: "calc(100% - 4px)",
                          borderRadius: "999px",
                          transition: "left 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                          boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                          zIndex: 1,
                          backgroundColor: "primary.main",
                          "[data-mui-color-scheme='dark'] &": {
                            backgroundColor: "primary.light",
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          justifyContent: "space-between",
                          position: "relative",
                          zIndex: 2,
                        }}
                      >
                        <Tooltip title="Search Mode">
                          <IconButton
                            onClick={() => setMode(DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH)}
                            aria-label="search mode"
                            sx={{ padding: "7px" }}
                          >
                            <Search
                              sx={{
                                color:
                                  mode === DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH
                                    ? "secondary.contrastText"
                                    : "primary.main",
                                transition: "color 0.3s ease-in-out",
                                "[data-mui-color-scheme='dark'] &": {
                                  color:
                                    mode === DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH
                                      ? "secondary.contrastText"
                                      : "primary.light",
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Edit Mode">
                          <IconButton
                            onClick={() => setMode(DOCUMENT_SYSTEM_PROMPT_NAME_EDIT)}
                            aria-label="edit mode"
                            sx={{ padding: "7px" }}
                          >
                            <Edit
                              sx={{
                                color:
                                  mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT ? "secondary.contrastText" : "primary.main",
                                transition: "color 0.3s ease-in-out",
                                "[data-mui-color-scheme='dark'] &": {
                                  color:
                                    mode === DOCUMENT_SYSTEM_PROMPT_NAME_EDIT
                                      ? "secondary.contrastText"
                                      : "primary.light",
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />

                  {/* Chat Input */}
                  <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder={
                        mode === DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH
                          ? selectedDocumentId
                            ? "Search this document..."
                            : "Search your documents..."
                          : selectedDocumentId
                            ? "Edit this document..."
                            : "Edit documents..."
                      }
                      disabled={isWorking}
                      multiline
                      maxRows={3}
                    />
                    <IconButton
                      onClick={sendChatCompletion}
                      disabled={isWorking || !inputMessage.trim()}
                      sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "&:disabled": { bgcolor: "grey.300" },
                        "[data-mui-color-scheme='dark'] &": {
                          bgcolor: "primary.dark",
                        },
                        "[data-mui-color-scheme='dark'] &:hover": {
                          bgcolor: "primary.main",
                        },
                        width: "40px",
                        height: "40px",
                        alignSelf: "center",
                      }}
                    >
                      <SendIcon color="white" />
                    </IconButton>
                  </Stack>

                  {/* Response Area */}
                  <Box
                    sx={{
                      flex: 1,
                      overflow: "auto",
                      bgcolor: "background.paper",
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    {assistantResponse ? (
                      <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "16px", lineHeight: 1.6 }}>
                        {assistantResponse}
                      </Typography>
                    ) : isWorking ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography color="text.secondary" variant="body2">
                          Thinking...
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        Ask a question about this document to get started
                      </Typography>
                    )}
                  </Box>

                  {/* Search Results Summary */}
                  {!isWorking && searchResults && searchResults.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Found {searchResults.length} search results
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          ) : (
            // Original chat interface when no specific document is selected
            <Stack spacing={2} sx={{ width: "100%" }}>
              {/* Chat Input */}
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder={selectedDocumentId ? "Search or edit this document..." : "Search your documents..."}
                  disabled={isWorking}
                  multiline
                  maxRows={4}
                />
                <IconButton
                  onClick={listening ? handleStopRecording : handleStartRecording}
                  disabled={isWorking}
                  sx={{
                    bgcolor: listening ? "error.main" : "primary.main",
                    color: "white",
                    "&:hover": {
                      bgcolor: listening ? "error.dark" : "primary.dark",
                    },
                    "&:disabled": {
                      backgroundColor: "grey.300",
                      color: "action.disabled",
                    },
                    width: "40px",
                    height: "40px",
                    mr: 1,
                  }}
                >
                  <MicrophoneIcon color="white" className={listening ? "pulsating" : ""} />
                </IconButton>
                <IconButton
                  onClick={sendChatCompletion}
                  disabled={isWorking || !inputMessage.trim()}
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "secondary.dark",
                    },
                    "&:disabled": {
                      backgroundColor: "grey.300",
                      color: "action.disabled",
                    },
                    width: "40px",
                    height: "40px",
                    mr: 1,
                  }}
                >
                  <SendIcon color="white" />
                </IconButton>
              </Stack>

              {/* Response */}
              {(isWorking || assistantResponse) && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    minHeight: 100,
                  }}
                >
                  {assistantResponse ? (
                    <Typography sx={{ whiteSpace: "pre-wrap", fontSize: "16px", lineHeight: 1.6 }}>
                      {assistantResponse}
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography color="text.secondary" variant="body2">
                        Thinking...
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Search Results Table */}
              {!isWorking && searchResults && searchResults.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Document Search Results ({Math.min(searchResults.length, DOCUMENT_SEARCH_RESULTS_LIMIT)} of{" "}
                    {searchResults.length})
                  </Typography>
                  <TableContainer component={Paper} className="tilley-table-container" sx={{ maxHeight: 400 }}>
                    <Table stickyHeader className="tilley-table" size="small">
                      <TableHead>
                        <TableRow className="tilley-table-header">
                          <TableCell className="tilley-table-header-cell">Document #</TableCell>
                          <TableCell className="tilley-table-header-cell">Document Type</TableCell>
                          <TableCell className="tilley-table-header-cell">Object</TableCell>
                          <TableCell className="tilley-table-header-cell">Name</TableCell>
                          <TableCell className="tilley-table-header-cell">Value</TableCell>
                          <TableCell className="tilley-table-header-cell">Date Added</TableCell>
                          <TableCell className="tilley-table-header-cell">View</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {searchResults.slice(0, DOCUMENT_SEARCH_RESULTS_LIMIT).map((result, index) => {
                          const documentId = result.documentId || result.DocumentId || "-";
                          const documentType = result.documentType || result.DocumentType || "-";
                          const object = result.object || result.Object || "-";
                          const name = result.name || result.Name || "-";
                          const rawValue = result.value || result.Value || "-";
                          const lastUpdate = result.lastUpdate || result.LastUpdate || null;
                          const chunkNumber = result._rg_chunk;

                          // Format document ID (show first 8 chars like the dropdown)
                          const documentNumber = documentId !== "-" ? documentId.substring(0, 8) : "-";

                          // Get chunk image URL from cached metadata
                          const metadata = documentMetadataCache[documentId];
                          let chunkImageUrl = "";
                          let chunkImageName = "";

                          if (metadata) {
                            // Look for chunk URLs array in metadata
                            const chunkUrls = metadata.chunkUrls || [];

                            // Get the specific chunk URL by chunk number (chunk numbers start at 1, array index starts at 0)
                            if (chunkUrls.length > 0 && chunkNumber !== undefined && chunkNumber > 0) {
                              chunkImageUrl = chunkUrls[chunkNumber - 1] || "";
                            }

                            // If we have chunk URL, extract the filename for display
                            if (chunkImageUrl) {
                              const urlParts = chunkImageUrl.split("/");
                              chunkImageName = urlParts[urlParts.length - 1];
                            }
                          }

                          // Fallback: construct chunk image name if not in metadata
                          if (!chunkImageName && documentId !== "-" && chunkNumber !== undefined) {
                            chunkImageName = `${documentId}_chunk_${chunkNumber}.jpg`;
                          }

                          // Format value - try to parse as number and format as money
                          let formattedValue = rawValue;
                          if (rawValue !== "-") {
                            const numValue = parseFloat(rawValue);
                            if (!isNaN(numValue)) {
                              formattedValue = formatNumberFinancial(numValue);
                            }
                          }

                          // Format date
                          const formattedDate = lastUpdate ? new Date(lastUpdate).toLocaleString() : "-";

                          return (
                            <TableRow key={index} className="tilley-table-row" hover>
                              <TableCell className="tilley-table-cell">
                                {documentId !== "-" ? (
                                  <Box
                                    component="span"
                                    onClick={() => setSelectedDocumentId(documentId)}
                                    sx={{
                                      color: "primary.main",
                                      cursor: "pointer",
                                      textDecoration: "underline",
                                      "&:hover": {
                                        color: "primary.dark",
                                      },
                                    }}
                                  >
                                    {documentNumber}
                                  </Box>
                                ) : (
                                  documentNumber
                                )}
                              </TableCell>
                              <TableCell className="tilley-table-cell">{documentType}</TableCell>
                              <TableCell className="tilley-table-cell">{object}</TableCell>
                              <TableCell className="tilley-table-cell">{name}</TableCell>
                              <TableCell className="tilley-table-cell">{formattedValue}</TableCell>
                              <TableCell className="tilley-table-cell">{formattedDate}</TableCell>
                              <TableCell className="tilley-table-cell" sx={{ textAlign: "center" }}>
                                {chunkImageUrl ? (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageDialogOpen(chunkImageUrl);
                                    }}
                                    sx={{
                                      color: "primary.main",
                                      "&:hover": {
                                        color: "primary.dark",
                                      },
                                    }}
                                  >
                                    <Description fontSize="small" />
                                  </IconButton>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Collapse>

      {/* Image Dialog */}
      <ChunkImageDialog
        open={imageDialogOpen}
        onClose={handleImageDialogClose}
        imageUrl={documentMetadataCache[selectedDocumentId]?.chunkUrls?.[currentImageIndex]}
        chunkUrls={documentMetadataCache[selectedDocumentId]?.chunkUrls || []}
        currentIndex={currentImageIndex}
        onIndexChange={handleIndexChange}
        showNavigation={true}
        documentId={selectedDocumentId}
        globalState={globalState}
      />
    </Box>
  );
};
